import React, { useState, useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { useLanguage } from '../context/LanguageContext';
import { MdDashboard, MdBusiness, MdExitToApp, MdSunny, MdNightlight, MdAccountCircle, MdEditNote, MdTraffic, MdWifi, MdMenu, MdClose, MdOutlineSignalCellularAlt, MdMonitorHeart } from 'react-icons/md';
import toast from 'react-hot-toast';
import JustLogo from '../assets/justLogo.png';
import CompanyProfileModal from './CompanyProfileModal';
import LanguageSwitcher from './LanguageSwitcher';
import api from '../utils/api';

export default function Navbar() {
    const { user, logout } = useAuth();
    const { theme, toggleTheme } = useTheme();
    const { t } = useLanguage();
    const navigate = useNavigate();
    const [showProfile, setShowProfile] = useState(false);
    const [manualEnabled, setManualEnabled] = useState(false);
    const [remoteAccessEnabled, setRemoteAccessEnabled] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);

    // Fetch manual data config for company users
    useEffect(() => {
        if (user?.role === 'company' && user?.companyId) {
            api.get(`/companies/${user.companyId}`)
                .then(res => {
                    setManualEnabled(res.data.data?.manualDataConfig?.enabled ?? false);
                })
                .catch(() => {});
            
            api.get(`/plc/company/${user.companyId}/remote-access`)
                .then(res => {
                    const data = res.data.data || [];
                    setRemoteAccessEnabled(data.length > 0);
                })
                .catch(() => {});
        }
    }, [user]);

    const handleLogout = () => {
        logout();
        toast.success(t('navbar.logout'));
        navigate('/login');
    };

    const adminLinks = [
        { to: '/admin', label: t('navbar.adminDashboard'), icon: <MdDashboard />, end: true },
        { to: '/admin/companies', label: t('navbar.companies'), icon: <MdBusiness /> },
        { to: '/admin/monitoring', label: t('navbar.monitoring'), icon: <MdMonitorHeart /> },
    ];

    const companyLinks = [
        { to: '/company', label: t('navbar.dashboard'), icon: <MdDashboard />, end: true },
        { to: '/company/data-signals', label: t('navbar.dataSignals'), icon: <MdOutlineSignalCellularAlt /> },
        { to: '/company/status-control', label: t('navbar.statusControl'), icon: <MdTraffic /> },
        ...(remoteAccessEnabled ? [{ to: '/company/remote-access', label: t('navbar.remoteAccess'), icon: <MdWifi /> }] : []),
        ...(manualEnabled ? [{ to: '/company/manual-data', label: t('navbar.manualData'), icon: <MdEditNote /> }] : []),
    ];

    const links = user?.role === 'admin' ? adminLinks : companyLinks;
    const initials = user?.username?.slice(0, 2).toUpperCase() || '?';

    return (
        <>
            <nav className="navbar">
                {/* Brand */}
                <div className="navbar-brand" onClick={() => navigate(user?.role === 'admin' ? '/admin' : '/company')} style={{ cursor: 'pointer' }}>
                    <div className="navbar-brand-icon">
                        <img
                            src={JustLogo}
                            alt="Nortech"
                            className="navbar-brand-logo"
                        />
                    </div>
                    <span className="navbar-brand-text">Nortech</span>
                </div>

                <div className="navbar-divider" />

                {/* Nav Links */}
                <div className="navbar-nav">
                    {links.map(link => (
                        <NavLink
                            key={link.to}
                            to={link.to}
                            end={link.end}
                            className={({ isActive }) => `navbar-nav-item${isActive ? ' active' : ''}`}
                        >
                            <span className="nav-icon" style={{ fontSize: 18, display: 'flex' }}>{link.icon}</span>
                            <span>{link.label}</span>
                        </NavLink>
                    ))}
                </div>

                {/* Hamburger toggle (mobile only) */}
                <button
                    className="navbar-hamburger-btn"
                    onClick={() => setMenuOpen(o => !o)}
                    aria-label="Menu"
                    aria-expanded={menuOpen}
                >
                    {menuOpen ? <MdClose /> : <MdMenu />}
                </button>

                {/* Right side */}
                <div className="navbar-right">
                    {/* Profile Button for Company */}
                    {user?.role === 'company' && (
                        <span className="navbar-desktop-inline">
                            <button
                                className="btn btn-secondary btn-sm"
                                style={{
                                    marginRight: 12,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 8,
                                    background: 'rgba(79,142,247,0.1)',
                                    color: 'var(--primary)',
                                    border: '1px solid rgba(79,142,247,0.2)',
                                    padding: '6px 14px'
                                }}
                                onClick={() => setShowProfile(true)}
                                id="open-profile-btn"
                            >
                                <MdAccountCircle size={18} />
                                <span style={{ fontSize: 13, fontWeight: 600 }}>{t('navbar.profile')}</span>
                            </button>
                        </span>
                    )}

                    {/* Language Switcher */}
                    <span className="navbar-desktop-inline">
                        <LanguageSwitcher style={{ marginRight: 8 }} />
                    </span>

                    {/* Theme Toggle */}
                    <button
                        id="theme-toggle-btn"
                        className="theme-toggle-btn"
                        onClick={toggleTheme}
                        title={theme === 'dark' ? 'Light Theme' : 'Dark Theme'}
                        style={{ marginRight: 8 }}
                    >
                        {theme === 'dark' ? <MdSunny /> : <MdNightlight />}
                    </button>

                    {/* User info */}
                    <div className="navbar-user">
                        <div className="navbar-user-avatar">{initials}</div>
                        <span className="navbar-user-name" style={{ maxWidth: 100, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.username}</span>
                    </div>

                    {/* Logout */}
                    <button
                        id="navbar-logout-btn"
                        className="navbar-logout-btn"
                        onClick={handleLogout}
                        title={t('navbar.logout')}
                    >
                        <MdExitToApp />
                    </button>
                </div>
            </nav>

            {menuOpen && (
                <>
                    <div className="navbar-mobile-backdrop" onClick={() => setMenuOpen(false)} />
                    <div className="navbar-mobile-menu">
                        {links.map(link => (
                            <NavLink
                                key={link.to}
                                to={link.to}
                                end={link.end}
                                onClick={() => setMenuOpen(false)}
                                className={({ isActive }) => `navbar-mobile-menu-item${isActive ? ' active' : ''}`}
                            >
                                <span className="nav-icon" style={{ fontSize: 18, display: 'flex' }}>{link.icon}</span>
                                <span>{link.label}</span>
                            </NavLink>
                        ))}

                        {user?.role === 'company' && (
                            <button
                                className="navbar-mobile-menu-item"
                                onClick={() => { setShowProfile(true); setMenuOpen(false); }}
                            >
                                <MdAccountCircle size={18} />
                                <span>{t('navbar.profile')}</span>
                            </button>
                        )}

                        <div className="navbar-mobile-lang">
                            <LanguageSwitcher style={{ width: '100%' }} />
                        </div>
                    </div>
                </>
            )}

            {showProfile && (
                <CompanyProfileModal onClose={() => setShowProfile(false)} />
            )}
        </>
    );
}
